import { MobileCargoList } from "@/pages/carrier-cargo-list/ui/mobile/cargo-list";
import { MobileSearchForms } from "@/pages/carrier-cargo-list/ui/mobile/search-forms";
import { MobileSelectOrderBy } from "@/pages/carrier-cargo-list/ui/mobile/select-order-by";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { fromIso8601DateTime } from "@/shared/lib/datetime";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BasePagination } from "@/shared/ui/base-pagination";
import { BasePaginationControl } from "@/shared/ui/base-pagination-control";
import {
  Box,
  CircularProgress,
  Collapse,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { addDays } from "date-fns";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import type { CarrierCargoListPageProps } from "../model";
import { CargoTable } from "./CargoTable";
import { SearchFormCard } from "./SearchForms";
import {
  type CarrierCargoListSearchParamKeys,
  OfferCargoSearchParamChipList,
} from "./search-param-chip-list";

export const CarrierCargoListPageUi: React.FC<CarrierCargoListPageProps> = (
  props,
) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isNewFilter, setIsNewFilter] = useState<boolean>();

  const filteredParamCount = useMemo(() => {
    let count = 0;
    if (isNewFilter) {
      count++;
    }
    if (
      props.getDraftOffersCargosParams?.loadingOnFrom ||
      props.getDraftOffersCargosParams?.loadingOnTo
    ) {
      count++;
    }
    if (
      props.getDraftOffersCargosParams?.unloadingOnFrom ||
      props.getDraftOffersCargosParams?.unloadingOnTo
    ) {
      count++;
    }
    for (const [key, value] of Object.entries(
      props.getDraftOffersCargosParams ?? {},
    )) {
      if (
        key === "loadingOnFrom" ||
        key === "loadingOnTo" ||
        key === "unloadingOnFrom" ||
        key === "unloadingOnTo"
      ) {
        continue;
      }
      if (value) {
        count++;
      }
    }
    return count;
  }, [props.getDraftOffersCargosParams, isNewFilter]);
  // TODO: BEに新着/提案済みのフィルターが実装されたら削除する
  const filteredDraftOfferCargos = useMemo(() => {
    if (
      props.draftOfferCargos.status !== "success" ||
      props.user.status !== "success"
    ) {
      return props.draftOfferCargos;
    }
    return {
      ...props.draftOfferCargos,
      data: {
        ...props.draftOfferCargos.data,
        data: props.draftOfferCargos.data.data.filter((cargo) => {
          if (isNewFilter) {
            // 24時間以内に作成された荷物
            const now = new Date();
            if (fromIso8601DateTime(cargo.createdAt) < addDays(now, -1)) {
              return false;
            }
          }
          if (cargo.process.status !== "success") {
            return true;
          }
          // 自分が提案したもののみ
          const isProposed =
            cargo.process.data.deliveryProposalOfferCargos?.some((offer) => {
              return (
                offer.company && offer.company.id === props.user.data?.companyId
              );
            });
          return props.selectedTab === "not_proposed"
            ? !isProposed
            : isProposed;
        }),
      },
    };
  }, [props.draftOfferCargos, props.user, isNewFilter, props.selectedTab]);

  const deleteParamChip = useCallback(
    (key: CarrierCargoListSearchParamKeys) => {
      if (key === "isNew") {
        setIsNewFilter(undefined);
      } else if (props.getDraftOffersCargosParams) {
        if (key === "loadingOn") {
          props.onGetDraftOffersCargosParamsChange({
            ...props.getDraftOffersCargosParams,
            loadingOnFrom: undefined,
            loadingOnTo: undefined,
          });
        } else if (key === "unloadingOn") {
          props.onGetDraftOffersCargosParamsChange({
            ...props.getDraftOffersCargosParams,
            unloadingOnFrom: undefined,
            unloadingOnTo: undefined,
          });
        } else if (
          key === "packingStyle" ||
          key === "vehicleShape" ||
          key === "shippingFee"
        ) {
          // These properties are not in the GetOffersCargosParams type but are used in the UI
          // We need to create a new object without the property
          const paramsObj = {
            ...props.getDraftOffersCargosParams,
          } as Record<string, unknown>;
          // Use object destructuring to create a new object without the property
          const { [key]: _, ...rest } = paramsObj;
          props.onGetDraftOffersCargosParamsChange(rest);
        } else {
          props.onGetDraftOffersCargosParamsChange({
            ...props.getDraftOffersCargosParams,
            [key]: undefined,
          });
        }
      }
    },
    [
      props.getDraftOffersCargosParams,
      props.onGetDraftOffersCargosParamsChange,
    ],
  );

  const pagination = props.draftOfferCargos.data?.pagination;

  return (
    <Stack px={{ xs: 2, sm: 4 }} pb={2} spacing={{ xs: 2, sm: 4 }}>
      <Stack
        direction="column"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <ScrollToTop />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          荷物一覧
        </Typography>
        <Tabs
          value={props.selectedTab === "not_proposed" ? 0 : 1}
          onChange={(_, value) => {
            props.onChangeTab(value === 0 ? "not_proposed" : "proposed");
          }}
        >
          <Tab
            icon={
              <BaseIcon
                name="search"
                sx={{
                  fontSize: { xs: undefined, sm: 28 },
                }}
              />
            }
            iconPosition="start"
            label="未提案"
            sx={{
              fontSize: { xs: undefined, sm: 20 },
              fontWeight: 700,
            }}
          />
          <Tab
            icon={
              <BaseIcon
                name="proposal"
                sx={{
                  fontSize: { xs: undefined, sm: 28 },
                }}
              />
            }
            iconPosition="start"
            label="提案済み"
            sx={{
              fontSize: { xs: undefined, sm: 20 },
              fontWeight: 700,
            }}
          />
        </Tabs>
      </Stack>

      <Stack spacing={1}>
        <Box display="flex" justifyContent="space-between">
          <BaseButton
            sx={{
              paddingLeft: {
                xs: 2,
                sm: 1,
              },
              paddingRight: {
                xs: 1,
                sm: 1,
              },
              borderColor: "grey.300",
              color: "grey.700",
            }}
            variant="outlined"
            startIcon={<BaseIcon name="filter" />}
            onClick={() => {
              setShowFilters((current) => !current);
              setShowMobileFilters((current) => !current);
            }}
          >
            <Typography fontWeight={700} mr={1}>
              フィルター
            </Typography>
            <Box
              width={"24px"}
              height={"24px"}
              display="flex"
              justifyContent="center"
              alignItems="center"
              p={0}
              borderRadius="50%"
              bgcolor="primary.500"
              color="white"
            >
              <Typography>{filteredParamCount}</Typography>
            </Box>
          </BaseButton>

          {/* TODO: APIがソートに対応したらちゃんと実装する */}
          <MobileSelectOrderBy value={"loadingOn"} onChange={(_) => 1} />
        </Box>

        {/* PC用の検索フォーム */}
        <Collapse
          in={showFilters}
          sx={{
            display: { xs: "none", sm: "block" },
            visibility: { xs: "hidden", sm: "visible" },
          }}
        >
          <SearchFormCard
            onGetOffersCargosParamsChange={
              props.onGetDraftOffersCargosParamsChange
            }
            onGetOffersCargosParamsReset={
              props.onGetDraftOffersCargosParamsReset
            }
            onIsNewFilterChange={setIsNewFilter}
          />
        </Collapse>

        {/* モバイル用の検索フォーム */}
        <MobileSearchForms
          open={showMobileFilters}
          onClose={() => setShowMobileFilters(false)}
          onGetOffersCargosParamsChange={
            props.onGetDraftOffersCargosParamsChange
          }
          onGetOffersCargosParamsReset={props.onGetDraftOffersCargosParamsReset}
          onIsNewFilterChange={setIsNewFilter}
        />

        {/* PC用のチップリスト */}
        <Collapse
          in={!showFilters}
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          <OfferCargoSearchParamChipList
            getDraftOffersCargosParams={props.getDraftOffersCargosParams}
            isNewFilter={isNewFilter}
            onDelete={deleteParamChip}
          />
        </Collapse>

        {/* モバイル用のチップリスト */}
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <OfferCargoSearchParamChipList
            getDraftOffersCargosParams={props.getDraftOffersCargosParams}
            isNewFilter={isNewFilter}
            onDelete={deleteParamChip}
          />
        </Box>

        {/* モバイル用のページネーションコントロール */}
        {pagination && (
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <BasePaginationControl
              page={pagination.page}
              perPage={pagination.perPage}
              totalCount={pagination.totalCount}
              onPerPageChange={pagination.onPerPageChange}
            />
          </Box>
        )}
      </Stack>

      {filteredDraftOfferCargos.status === "success" &&
      props.user.status === "success" ? (
        <>
          {/* PC用の一覧 */}
          <Stack
            direction="column"
            spacing={2}
            display={{ xs: "none", sm: "block" }}
          >
            {pagination && (
              <BasePaginationControl
                page={pagination.page}
                perPage={pagination.perPage}
                totalCount={pagination.totalCount}
                onPerPageChange={pagination.onPerPageChange}
              />
            )}
            <CargoTable
              user={props.user.data}
              draftOfferCargos={filteredDraftOfferCargos.data}
            />
            {pagination && (
              <BasePagination
                page={pagination.page}
                perPage={pagination.perPage}
                totalCount={pagination.totalCount}
                onPageChange={pagination.onPageChange}
              />
            )}
          </Stack>

          {/* モバイル用の一覧 */}
          <Stack
            direction="column"
            alignItems="center"
            display={{ xs: "flex", sm: "none" }}
          >
            <Box alignSelf="stretch" mx={-2} mb={2}>
              <MobileCargoList
                user={props.user.data}
                draftOfferCargos={filteredDraftOfferCargos.data}
              />
            </Box>
            {pagination && (
              <BasePagination
                page={pagination.page}
                perPage={pagination.perPage}
                totalCount={pagination.totalCount}
                onPageChange={pagination.onPageChange}
              />
            )}
          </Stack>
        </>
      ) : filteredDraftOfferCargos.status === "loading" ? (
        <Stack justifyContent="center" alignItems="center" p={4}>
          <CircularProgress />
        </Stack>
      ) : (
        <Typography color="error">エラーが発生しました</Typography>
      )}
    </Stack>
  );
};
